import { TooltipWrapper } from "@/components/molecules/TooltipWrapper/TooltipWrapper";
import { isoBandsLayerConfig } from "@/constants/layerConfigAttributes";
import {
  type IsoBandsFillMode,
  createRandomIsoBandColors,
  parseIsoBandsFillColors,
  parseIsoBandsFillMode,
  parseIsoBandsFillReverse,
  resolveIsoBandColors,
  resolveIsoBandEntries,
  serializeIsoBandsFillColors,
} from "@/layers/geojson/isoBandsFillUtils";
import {
  type IsoBandsLayer,
  type IsoLayerCustomOptions,
  readIsoLayerCustomOptions,
} from "@/layers/geojson/isoLayerShared";
import { Button } from "@/overlay/components/CommonParts/buttons";
import { ColorPickerInput } from "@/overlay/components/LayerStackWindow/LayerFormElements";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useMemo, useState } from "react";
import styles from "./IsoBandsFillColorsEditor.module.scss";
import { asObject } from "./IsoLayerFormUtils";

export const ISO_BANDS_FILL_EDITOR_TEST_IDS = {
  root: "IsoBandsFillEditor-root",
  randomMode: "IsoBandsFillEditor-random-mode",
  monochromaticMode: "IsoBandsFillEditor-monochromatic-mode",
  reverseOrder: "IsoBandsFillEditor-reverse-order",
  save: "IsoBandsFillEditor-save",
  emptyState: "IsoBandsFillEditor-empty-state",
};

type IsoBandsFillColorsEditorProps = {
  layer: Pick<IsoBandsLayer, "values" | "value_range" | "custom_options">;
  onSave: (customOptions: IsoLayerCustomOptions) => void;
  onClose: () => void;
};

const compactColorPickerFormFieldOptions = {
  alignTop: true,
  background: false,
  slimPadding: true,
};

const getSelectionTypeLabel = (layer: Pick<IsoBandsLayer, "values" | "value_range" | "custom_options">): string => {
  const customOptions = readIsoLayerCustomOptions(layer.custom_options);
  if (customOptions.isoband_intervals?.trim()) {
    return "Intervals";
  }
  if (layer.values?.trim()) {
    return "Values";
  }
  if (layer.value_range?.trim()) {
    return "Ranges";
  }
  return "Values";
};

export function IsoBandsFillColorsEditor(props: IsoBandsFillColorsEditorProps) {
  const { layer, onSave, onClose } = props;
  const customOptions = readIsoLayerCustomOptions(layer.custom_options);
  const bandEntries = useMemo(
    () =>
      resolveIsoBandEntries({
        values: layer.values,
        valueRange: layer.value_range,
        isobandIntervals: customOptions.isoband_intervals,
      }),
    [customOptions.isoband_intervals, layer.value_range, layer.values],
  );
  const selectionTypeLabel = getSelectionTypeLabel(layer);
  const initialBaseColor = customOptions.fill_color ?? isoBandsLayerConfig.defaultFillColor;
  const initialMode = parseIsoBandsFillMode(customOptions.fill_color_mode);
  const initialReverse = parseIsoBandsFillReverse(customOptions.fill_reverse);
  const initialRandomColors =
    parseIsoBandsFillColors(customOptions.fill_colors).length === bandEntries.length
      ? parseIsoBandsFillColors(customOptions.fill_colors)
      : createRandomIsoBandColors(bandEntries.length, initialBaseColor);

  const [mode, setMode] = useState<IsoBandsFillMode>(initialMode);
  const [reverse, setReverse] = useState(initialReverse);
  const [baseColor, setBaseColor] = useState(initialBaseColor);
  const [randomColors, setRandomColors] = useState(initialRandomColors);

  const canonicalRandomColors = randomColors.length === bandEntries.length ? randomColors : initialRandomColors;
  const visibleColors =
    mode === "monochromatic"
      ? resolveIsoBandColors({
          values: layer.values,
          valueRange: layer.value_range,
          isobandIntervals: customOptions.isoband_intervals,
          baseColor,
          fillColorMode: "monochromatic",
          fillReverse: reverse ? "true" : null,
        })
      : reverse
        ? canonicalRandomColors.slice().reverse()
        : canonicalRandomColors;

  const handleModeChange = (nextMode: IsoBandsFillMode) => {
    if (nextMode === mode) {
      return;
    }

    if (nextMode === "random") {
      const nextColors = reverse ? visibleColors.slice().reverse() : visibleColors.slice();
      setRandomColors(nextColors);
    } else if (visibleColors[0]) {
      setBaseColor(visibleColors[0]);
    }

    setMode(nextMode);
  };

  const handleRandomColorChange = (visibleIndex: number, color: string) => {
    const targetIndex = reverse ? canonicalRandomColors.length - 1 - visibleIndex : visibleIndex;
    setRandomColors((prev) =>
      prev.map((existingColor, index) => {
        if (index !== targetIndex) {
          return existingColor;
        }
        return color;
      }),
    );
  };

  const handleSave = () => {
    const nextCustomOptions: IsoLayerCustomOptions = {
      ...asObject(layer.custom_options),
      fill_color: mode === "monochromatic" ? baseColor : null,
      fill_color_mode: mode,
      fill_reverse: reverse ? "true" : null,
      fill_colors: mode === "random" ? serializeIsoBandsFillColors(canonicalRandomColors) : null,
    };

    onSave(nextCustomOptions);
    onClose();
  };

  return (
    <div className={styles.editor} data-testid={ISO_BANDS_FILL_EDITOR_TEST_IDS.root}>
      <div className={styles.title}>
        <Trans>Isobands Fill Colors</Trans>
      </div>

      <div className={styles.meta}>
        <div>
          <div className={styles.metaLabel}>
            <Trans>Selection type</Trans>
          </div>
          <div className={styles.metaValue}>{selectionTypeLabel}</div>
        </div>
        <div>
          <div className={styles.metaLabel}>
            <Trans>Bands</Trans>
          </div>
          <div className={styles.metaValue}>{bandEntries.length}</div>
        </div>
      </div>

      <div>
        <div className={styles.modeHeader}>
          <div className={styles.metaValue}>
            <Trans>Color mode</Trans>
          </div>
          <TooltipWrapper tooltipText={t`Reverse order`} position="top" tail>
            <button
              className={styles.reverseButton}
              type="button"
              onClick={() => setReverse((prev) => !prev)}
              aria-label={t`Reverse order`}
              data-testid={ISO_BANDS_FILL_EDITOR_TEST_IDS.reverseOrder}
            >
              <span className="material-icons">swap_vert</span>
            </button>
          </TooltipWrapper>
        </div>
        <div className={styles.section}>
          <div className={styles.radioGroup}>
            <label className={styles.radioOption}>
              <span>
                <Trans>Random colors</Trans>
              </span>
              <input
                type="radio"
                name="iso-bands-fill-mode"
                checked={mode === "random"}
                onChange={() => handleModeChange("random")}
                data-testid={ISO_BANDS_FILL_EDITOR_TEST_IDS.randomMode}
              />
            </label>
            <label className={styles.radioOption}>
              <span>
                <Trans>Monochromatic</Trans>
              </span>
              <input
                type="radio"
                name="iso-bands-fill-mode"
                checked={mode === "monochromatic"}
                onChange={() => handleModeChange("monochromatic")}
                data-testid={ISO_BANDS_FILL_EDITOR_TEST_IDS.monochromaticMode}
              />
            </label>
          </div>
        </div>
      </div>

      {mode === "monochromatic" && bandEntries.length > 0 && (
        <div className={styles.baseColorRow}>
          <ColorPickerInput
            label=""
            ariaLabel={t`Base color`}
            value={baseColor}
            change={setBaseColor}
            withinPortal={false}
            formFieldOptions={compactColorPickerFormFieldOptions}
          />
          <div>
            <div className={styles.rowTitle}>
              <Trans>Base color</Trans>
            </div>
            <div className={styles.rowSubtitle}>
              <Trans>Derived shades and tints update automatically.</Trans>
            </div>
          </div>
        </div>
      )}

      {bandEntries.length === 0 ? (
        <div className={styles.emptyState} data-testid={ISO_BANDS_FILL_EDITOR_TEST_IDS.emptyState}>
          <Trans>Configure values, ranges, or intervals first to manage fill colors.</Trans>
        </div>
      ) : (
        <div className={styles.rows}>
          {bandEntries.map((entry, index) => (
            <div className={styles.row} key={entry.key}>
              {mode === "random" ? (
                <ColorPickerInput
                  label=""
                  ariaLabel={`${t`Band color`} ${entry.label}`}
                  value={visibleColors[index] ?? baseColor}
                  change={(color) => handleRandomColorChange(index, color)}
                  withinPortal={false}
                  formFieldOptions={compactColorPickerFormFieldOptions}
                />
              ) : (
                <div
                  className={styles.previewSwatch}
                  style={{ backgroundColor: visibleColors[index] ?? baseColor }}
                  aria-hidden="true"
                />
              )}
              <div className={styles.rowLabel}>
                <div className={styles.rowTitle}>{entry.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={styles.actions}>
        <Button type="button" onClick={onClose}>
          <Trans>Cancel</Trans>
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          primary
          disabled={bandEntries.length === 0}
          data-testid={ISO_BANDS_FILL_EDITOR_TEST_IDS.save}
        >
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  );
}
